/**
 * @fileoverview Error Tracking Service for Application-Wide Error Management
 * 
 * This module provides comprehensive error tracking and reporting capabilities,
 * integrating with both Sentry (for real-time monitoring) and Supabase (for
 * internal analytics and admin alerts). Handles frontend errors, unhandled
 * rejections, network failures, and React Query errors.
 * 
 * @module lib/errorTracking
 * 
 * Key Features:
 * - Dual reporting: Sentry for monitoring + Supabase for database persistence
 * - Automatic error categorization and severity detection
 * - Sensitive data sanitization (passwords, tokens, API keys)
 * - Session tracking for error correlation
 * - Browser context capture for debugging
 * - Admin alerts for critical errors (especially PO-related)
 * - React Query error enrichment
 * 
 * Common Use Cases:
 * 1. Automatic error boundary integration
 * 2. React Query mutation/query error tracking
 * 3. Network request failure logging
 * 4. Unhandled promise rejection capture
 * 5. Custom business logic error reporting
 * 
 * @example React Error Boundary integration
 * ```typescript
 * import { errorTracking } from '@/lib/errorTracking';
 * 
 * class ErrorBoundary extends React.Component {
 *   componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
 *     errorTracking.captureError(error, errorInfo);
 *   }
 * }
 * ```
 * 
 * @example React Query mutation error
 * ```typescript
 * const createJob = useMutation({
 *   mutationFn: createJobFn,
 *   onError: (error, variables) => {
 *     errorTracking.captureQueryError(
 *       error as Error,
 *       'mutation',
 *       ['jobs', 'create'],
 *       variables
 *     );
 *   }
 * });
 * ```
 * 
 * @example Custom error with context
 * ```typescript
 * try {
 *   await processPayment(orderId);
 * } catch (error) {
 *   await errorTracking.captureCustomError(
 *     'Payment processing failed',
 *     'critical',
 *     { orderId, amount, paymentMethod }
 *   );
 * }
 * ```
 */
import { supabase } from '@/integrations/supabase/client';
import * as Sentry from '@sentry/react';
import type { EdgeFunctionError } from './edgeFunctionClient';

/**
 * PostgrestError structure from Supabase SDK
 * 
 * Represents database errors returned by Supabase PostgREST API.
 * These are plain objects, NOT Error instances, which causes issues
 * when passed directly to Sentry.captureException().
 */
export interface PostgrestError {
  code: string;
  message: string;
  details: string | null;
  hint: string | null;
}

/**
 * Type guard to detect PostgrestError objects from Supabase
 * 
 * PostgrestError is a plain object with code/message/details/hint properties,
 * NOT an Error instance. This distinguishes it from EdgeFunctionError and
 * standard JavaScript Error objects.
 * 
 * @param error - Unknown error to check
 * @returns True if error matches PostgrestError structure
 */
export function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    'message' in error &&
    !('name' in error && (error as any).name === 'EdgeFunctionError') &&
    typeof (error as PostgrestError).code === 'string' &&
    typeof (error as PostgrestError).message === 'string'
  );
}

/**
 * Extracts table name from React Query key
 * 
 * Common patterns: ['jobs', ...], ['profiles', userId], etc.
 * The first string element is typically the table/resource name.
 * 
 * @param queryKey - React Query key array
 * @returns Table name or undefined
 */
export function extractTableFromQueryKey(queryKey: unknown[] | readonly unknown[]): string | undefined {
  if (Array.isArray(queryKey) && queryKey.length > 0 && typeof queryKey[0] === 'string') {
    return queryKey[0];
  }
  return undefined;
}

/**
 * Error data structure for database persistence
 * 
 * @property error_id - Unique hash based on error message and stack
 * @property error_type - Source of the error (frontend, backend, etc.)
 * @property error_category - Type of error for filtering and analysis
 * @property severity - Impact level for prioritization and alerting
 * @property message - Error message (truncated to 500 chars)
 * @property stack_trace - Full stack trace (truncated to 5000 chars)
 * @property component_path - React component that threw the error
 * @property user_id - Authenticated user ID if available
 * @property organization_id - User's organization for tenant filtering
 * @property session_id - Unique session identifier for correlation
 * @property url - Page URL where error occurred
 * @property user_agent - Browser user agent string
 * @property browser_info - Parsed browser and viewport information
 * @property context - Additional error context (sanitized)
 */
interface ErrorData {
  error_id: string;
  error_type: 'frontend' | 'backend' | 'edge_function' | 'database';
  error_category: 'runtime' | 'validation' | 'network' | 'auth' | 'permission' | 'other';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  stack_trace?: string;
  component_path?: string;
  user_id?: string;
  organization_id?: string;
  session_id?: string;
  url?: string;
  user_agent?: string;
  browser_info?: any;
  context?: any;
}

/**
 * Error tracking service for centralized error management
 * 
 * @remarks
 * - Singleton instance exported as `errorTracking`
 * - Automatically enriches errors with user and session context
 * - Prevents duplicate reporting with `isReporting` flag
 * - Sanitizes sensitive data before sending to external services
 */
class ErrorTrackingService {
  private sessionId: string;
  private isReporting = false;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Generates a unique session identifier
   * 
   * @internal
   * @returns Unique session ID combining timestamp and random string
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generates a consistent error ID for deduplication
   * 
   * @internal
   * @param error - Error object to hash
   * @param component - Optional component path
   * @returns Hash-based error ID
   * 
   * @remarks
   * Same error in same component will generate same ID for grouping
   */
  private generateErrorId(error: Error, component?: string): string {
    const baseString = `${error.message}${error.stack?.substring(0, 200) || ''}${component || ''}`;
    return this.hashString(baseString);
  }

  /**
   * Simple string hashing function
   * 
   * @internal
   * @param str - String to hash
   * @returns Base36-encoded hash value
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Captures browser and viewport information
   * 
   * @internal
   * @returns Browser context object
   */
  private getBrowserInfo() {
    const ua = navigator.userAgent;
    return {
      userAgent: ua,
      language: navigator.language,
      platform: navigator.platform,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }

  /**
   * Automatically determines error severity based on content
   * 
   * @internal
   * @param error - Error object to analyze
   * @param context - Additional context with optional severity override
   * @returns Severity level
   * 
   * @remarks
   * - Auth errors: high severity
   * - Network errors: medium severity
   * - Context can override with explicit severity
   */
  private determineSeverity(error: Error, context?: any): 'low' | 'medium' | 'high' | 'critical' {
    const message = error.message.toLowerCase();
    
    if (message.includes('auth') || message.includes('unauthorized')) return 'high';
    if (message.includes('network') || message.includes('fetch')) return 'medium';
    if (context?.severity) return context.severity;
    
    return 'medium';
  }

  /**
   * Categorizes error by type for filtering and analysis
   * 
   * @internal
   * @param error - Error object to categorize
   * @returns Error category
   */
  private categorizeError(error: Error): 'runtime' | 'validation' | 'network' | 'auth' | 'permission' | 'other' {
    const message = error.message.toLowerCase();
    
    if (message.includes('auth') || message.includes('token')) return 'auth';
    if (message.includes('permission') || message.includes('forbidden')) return 'permission';
    if (message.includes('network') || message.includes('fetch') || message.includes('timeout')) return 'network';
    if (message.includes('validation') || message.includes('invalid')) return 'validation';
    
    return 'runtime';
  }

  /**
   * Removes sensitive information from error context
   * 
   * @internal
   * @param data - Data to sanitize
   * @returns Sanitized data with sensitive values redacted
   * 
   * @remarks
   * Recursively redacts: password, token, secret, apikey, api_key, authorization
   */
  private sanitizeData(data: any): any {
    if (!data) return data;
    
    const sensitiveKeys = ['password', 'token', 'secret', 'apikey', 'api_key', 'authorization'];
    
    if (typeof data === 'object') {
      const sanitized = { ...data };
      for (const key in sanitized) {
        if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof sanitized[key] === 'object') {
          sanitized[key] = this.sanitizeData(sanitized[key]);
        }
      }
      return sanitized;
    }
    
    return data;
  }

  /**
   * Captures and reports an error to both Sentry and Supabase
   * 
   * Main error tracking method. Enriches error with user context, browser info,
   * and session data before sending to monitoring services.
   * 
   * @param error - Error object to capture
   * @param errorInfo - Additional error context (React ErrorInfo, etc.)
   * @returns Promise<void>
   * 
   * @example React Error Boundary
   * ```typescript
   * componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
   *   errorTracking.captureError(error, errorInfo);
   * }
   * ```
   * 
   * @example Try-catch block
   * ```typescript
   * try {
   *   await riskyOperation();
   * } catch (error) {
   *   await errorTracking.captureError(error as Error, {
   *     operation: 'riskyOperation',
   *     params: { id: 123 }
   *   });
   * }
   * ```
   * 
   * @remarks
   * - Automatically retrieves user and organization context
   * - Prevents duplicate reporting with isReporting flag
   * - Sanitizes sensitive data before sending
   * - Sends to Sentry with full context enrichment
   * - Stores in Supabase via edge function
   * - Triggers admin alerts for critical PO errors
   */
  async captureError(error: Error, errorInfo?: any): Promise<void> {
    if (this.isReporting) return;

    try {
      this.isReporting = true;

      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = user 
        ? await supabase.from('profiles').select('organization_id').eq('user_id', user.id).maybeSingle()
        : { data: null };

      const errorData: ErrorData = {
        error_id: this.generateErrorId(error, errorInfo?.componentStack),
        error_type: 'frontend',
        error_category: this.categorizeError(error),
        severity: this.determineSeverity(error, errorInfo),
        message: error.message.substring(0, 500),
        stack_trace: error.stack?.substring(0, 5000),
        component_path: errorInfo?.componentStack?.split('\n')[1]?.trim(),
        user_id: user?.id,
        organization_id: profile?.organization_id,
        session_id: this.sessionId,
        url: window.location.href,
        user_agent: navigator.userAgent,
        browser_info: this.getBrowserInfo(),
        context: this.sanitizeData(errorInfo)
      };

      console.error('[ErrorTracking] Captured error:', {
        error_id: errorData.error_id,
        message: error.message,
        category: errorData.error_category,
        severity: errorData.severity,
        component: errorData.component_path,
      });
      
      try {
        const eventId = Sentry.withScope((scope) => {
          scope.setUser({ id: user?.id });
          scope.setContext('organization', { id: profile?.organization_id });
          scope.setContext('session', { id: this.sessionId });
          scope.setContext('error_tracking', {
            error_id: errorData.error_id,
            category: errorData.error_category,
            severity: errorData.severity
          });
          if (errorInfo) {
            scope.setContext('additional', this.sanitizeData(errorInfo));
          }
          return Sentry.captureException(error);
        });
        console.log('[ErrorTracking] Sent to Sentry with event ID:', eventId);
        
        await Sentry.flush(2000);
      } catch (sentryError) {
        console.error('[ErrorTracking] Failed to send to Sentry:', sentryError);
      }

      await this.reportError(errorData);
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    } finally {
      this.isReporting = false;
    }
  }

  /**
   * Captures unhandled promise rejections
   * 
   * @param event - PromiseRejectionEvent from window listener
   * @returns Promise<void>
   * 
   * @example Setup listener
   * ```typescript
   * window.addEventListener('unhandledrejection', (event) => {
   *   errorTracking.captureUnhandledRejection(event);
   * });
   * ```
   */
  async captureUnhandledRejection(event: PromiseRejectionEvent): Promise<void> {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    await this.captureError(error, { type: 'unhandled_rejection' });
  }

  /**
   * Captures network errors from failed HTTP requests
   * 
   * @param response - Failed Response object
   * @param context - Additional context about the request
   * @returns Promise<void>
   * 
   * @example Fetch error handling
   * ```typescript
   * const response = await fetch('/api/endpoint');
   * if (!response.ok) {
   *   await errorTracking.captureNetworkError(response, {
   *     endpoint: '/api/endpoint',
   *     method: 'POST',
   *     body: requestBody
   *   });
   * }
   * ```
   */
  async captureNetworkError(response: Response, context: any): Promise<void> {
    const error = new Error(`Network error: ${response.status} ${response.statusText}`);
    await this.captureError(error, {
      ...context,
      type: 'network',
      status: response.status,
      url: response.url
    });
  }

  /**
   * Captures React Query errors with enriched context
   * 
   * Special handling for React Query errors with query key and variable tracking.
   * Enriches Sentry with React Query-specific context for better debugging.
   * 
   * @param error - Error from React Query
   * @param operation - 'query' or 'mutation'
   * @param queryKey - React Query key for identification
   * @param variables - Mutation variables if applicable
   * @returns Promise<void>
   * 
   * @example Mutation error
   * ```typescript
   * const mutation = useMutation({
   *   mutationFn: createJobFn,
   *   onError: (error, variables, context) => {
   *     errorTracking.captureQueryError(
   *       error as Error,
   *       'mutation',
   *       ['jobs', 'create'],
   *       variables
   *     );
   *   }
   * });
   * ```
   */
  async captureQueryError(
    error: Error, 
    operation: 'query' | 'mutation',
    queryKey?: readonly unknown[] | unknown[],
    variables?: any
  ): Promise<void> {
    Sentry.withScope((scope) => {
      scope.setTag('error_source', 'react_query');
      scope.setTag('query_operation', operation);
      
      if (queryKey) {
        scope.setContext('react_query', {
          operation,
          queryKey: this.sanitizeData(queryKey),
          queryKeyString: JSON.stringify(queryKey),
        });
      }
      
      if (variables) {
        scope.setContext('mutation_variables', this.sanitizeData(variables));
      }
      
      if (queryKey && Array.isArray(queryKey)) {
        const mainKey = queryKey[0];
        scope.setFingerprint([
          'react-query',
          operation,
          String(mainKey),
          error.message,
        ]);
      }
      
      Sentry.captureException(error);
    });

    await this.captureError(error, {
      type: 'react_query',
      operation,
      queryKey: this.sanitizeData(queryKey),
      variables: this.sanitizeData(variables),
    });
  }

  /**
   * Captures custom error messages with explicit severity
   * 
   * Use for business logic errors or custom validation failures.
   * 
   * @param message - Custom error message
   * @param severity - Explicit severity level
   * @param context - Additional context for debugging
   * @returns Promise<void>
   * 
   * @example Business logic error
   * ```typescript
   * if (orderTotal > accountBalance) {
   *   await errorTracking.captureCustomError(
   *     'Insufficient funds for order',
   *     'high',
   *     { orderId, orderTotal, accountBalance }
   *   );
   * }
   * ```
   */
  async captureCustomError(message: string, severity: 'low' | 'medium' | 'high' | 'critical', context: any): Promise<void> {
    Sentry.withScope((scope) => {
      scope.setLevel(severity === 'critical' ? 'fatal' : severity === 'high' ? 'error' : severity === 'medium' ? 'warning' : 'info');
      if (context) {
        scope.setContext('custom_error', this.sanitizeData(context));
      }
      Sentry.captureMessage(message, severity === 'critical' ? 'fatal' : severity === 'high' ? 'error' : 'warning');
    });

    const error = new Error(message);
    await this.captureError(error, { ...context, severity });
  }

  /**
   * Captures Edge Function errors with enriched context for Sentry
   * 
   * Specialized handler for EdgeFunctionError that:
   * - Sets custom fingerprints for better grouping by function name
   * - Adds rich tags for filtering (function_name, http_status)
   * - Captures request/response context for debugging
   * 
   * @param error - EdgeFunctionError from edgeFunctionClient
   * @param additionalContext - Extra context (mutation variables, etc.)
   * @returns Promise<void>
   * 
   * @example Usage with React Query
   * ```typescript
   * if (error.name === 'EdgeFunctionError') {
   *   await errorTracking.captureEdgeFunctionError(error, {
   *     mutationKey: ['payment', 'approve'],
   *     variables: { queueId },
   *   });
   * }
   * ```
   */
  async captureEdgeFunctionError(
    error: EdgeFunctionError,
    additionalContext?: Record<string, unknown>
  ): Promise<void> {
    // Runtime property validation for safety
    const functionName = error?.functionName || 'unknown';
    const httpStatus = error?.httpStatus;
    const callerOperation = error?.callerOperation;
    const requestUrl = error?.requestUrl || 'unknown';
    const httpMethod = error?.httpMethod || 'POST';
    const durationMs = error?.durationMs || 0;
    const requestBody = error?.requestBody;
    const responseBody = error?.responseBody;

    console.error('[ErrorTracking] Edge Function error:', {
      functionName,
      httpStatus,
      callerOperation,
      requestUrl,
      responseBody,
    });

    Sentry.withScope((scope) => {
      // Custom fingerprint for better grouping by function and status
      scope.setFingerprint([
        'edge-function',
        functionName,
        httpStatus?.toString() || 'network-error',
      ]);

      // Tags for filtering in Sentry
      scope.setTag('error_source', 'edge_function');
      scope.setTag('edge_function_name', functionName);
      scope.setTag('http_status', httpStatus?.toString() || 'unknown');
      if (callerOperation) {
        scope.setTag('caller_operation', callerOperation);
      }

      // Rich context matching DevTools visibility
      scope.setContext('edge_function_request', {
        function_name: functionName,
        request_url: requestUrl,
        http_method: httpMethod,
        caller_operation: callerOperation,
        request_body: this.sanitizeData(requestBody),
      });

      scope.setContext('edge_function_response', {
        http_status: httpStatus,
        response_body: this.sanitizeData(responseBody),
        duration_ms: durationMs,
      });

      if (additionalContext) {
        scope.setContext('additional', this.sanitizeData(additionalContext));
      }

      // Set severity based on HTTP status
      if (httpStatus && httpStatus >= 500) {
        scope.setLevel('error');
      } else if (httpStatus && httpStatus >= 400) {
        scope.setLevel('warning');
      } else {
        scope.setLevel('error'); // Network errors
      }

      Sentry.captureException(error);
    });

    // Report to Supabase only (Sentry already captured above - avoid double capture)
    await this.reportToSupabaseOnly(error, {
      ...additionalContext,
      error_type: 'edge_function',
      function_name: functionName,
      http_status: httpStatus,
      caller_operation: callerOperation,
      duration_ms: durationMs,
    });
  }

  /**
   * Captures Supabase PostgrestError with enriched context for Sentry
   * 
   * Converts plain PostgrestError objects into proper Error instances with:
   * - Custom fingerprints for better grouping by error code and table
   * - Rich tags for filtering (postgres_code, table, operation)
   * - Full PostgrestError context (code, message, details, hint)
   * 
   * @param error - PostgrestError from Supabase database operations
   * @param context - Additional context (table name, operation type, query key)
   * @returns Promise<void>
   * 
   * @example Usage with React Query global handler
   * ```typescript
   * if (isPostgrestError(error)) {
   *   await errorTracking.captureSupabaseError(error, {
   *     table: 'jobs',
   *     operation: 'select',
   *     queryKey: query.queryKey,
   *   });
   * }
   * ```
   */
  async captureSupabaseError(
    error: PostgrestError,
    context: {
      table?: string;
      operation?: 'select' | 'insert' | 'update' | 'delete' | 'rpc';
      queryKey?: unknown[] | readonly unknown[];
      mutationKey?: unknown[] | readonly unknown[];
      variables?: unknown;
    }
  ): Promise<void> {
    // Convert to proper Error instance with descriptive message
    const errorInstance = new Error(
      `Supabase ${context.operation || 'query'} failed: ${error.message}`
    );
    errorInstance.name = 'PostgrestError';

    console.error('[ErrorTracking] Supabase PostgrestError:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
      table: context.table,
      operation: context.operation,
    });

    Sentry.withScope((scope) => {
      // Custom fingerprint for better grouping by error code and table
      scope.setFingerprint([
        'supabase',
        error.code,
        context.table || 'unknown',
      ]);

      // Tags for filtering in Sentry
      scope.setTag('error_source', 'supabase');
      scope.setTag('postgres_code', error.code);
      if (context.table) scope.setTag('table', context.table);
      if (context.operation) scope.setTag('operation', context.operation);

      // Rich context with PostgrestError details
      scope.setContext('supabase_error', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        table: context.table,
        operation: context.operation,
      });

      // Add React Query context
      const queryKey = context.queryKey || context.mutationKey;
      if (queryKey) {
        scope.setContext('react_query', {
          queryKey: this.sanitizeData(queryKey),
          queryKeyString: JSON.stringify(queryKey),
        });
      }

      if (context.variables) {
        scope.setContext('mutation_variables', this.sanitizeData(context.variables));
      }

      // Set severity based on error code
      // PGRST codes are PostgREST errors, numeric codes are Postgres errors
      if (error.code.startsWith('PGRST')) {
        scope.setLevel('warning'); // PostgREST errors (often client-side issues)
      } else if (error.code.startsWith('23')) {
        scope.setLevel('warning'); // Constraint violations (often validation issues)
      } else {
        scope.setLevel('error'); // Other database errors
      }

      Sentry.captureException(errorInstance);
    });

    // Report to Supabase only (Sentry already captured above)
    await this.reportToSupabaseOnly(errorInstance, {
      error_type: 'database',
      postgres_code: error.code,
      postgres_details: error.details,
      postgres_hint: error.hint,
      table: context.table,
      operation: context.operation,
    });
  }

  /**
   * Reports error to Supabase without triggering Sentry capture
   * 
   * Used internally to avoid double Sentry capture when the error
   * has already been sent to Sentry with custom context.
   * 
   * @internal
   * @param error - The error object
   * @param context - Additional context for the error
   */
  private async reportToSupabaseOnly(
    error: Error,
    context?: Record<string, unknown>
  ): Promise<void> {
    // Get user context inline (same pattern as captureError)
    const { data: { user } } = await supabase.auth.getUser();
    const { data: profile } = user 
      ? await supabase.from('profiles').select('organization_id').eq('user_id', user.id).maybeSingle()
      : { data: null };

    const errorData: ErrorData = {
      error_id: this.generateErrorId(error),
      error_type: 'edge_function',
      error_category: 'network',
      message: error.message?.substring(0, 500) || 'Unknown error',
      stack_trace: error.stack?.substring(0, 5000),
      severity: 'medium',
      user_id: user?.id,
      organization_id: profile?.organization_id,
      session_id: this.sessionId,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      context: this.sanitizeData(context),
    };

    await this.reportError(errorData);
  }

  /**
   * Sends error data to Supabase edge function
   * 
   * @internal
   * @param errorData - Error data to persist
   * @returns Promise<void>
   * 
   * @remarks
   * - Stores error in application_errors table via edge function
   * - Triggers admin email alerts for critical PO-related errors
   * - Does not throw on failure to prevent error loops
   */
  private async reportError(errorData: ErrorData): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke('report-error', {
        body: errorData
      });

      if (error) {
        console.error('Error reporting failed:', error);
      }

      if ((errorData.severity === 'critical' || errorData.severity === 'high') && 
          errorData.message?.includes('PO Creation')) {
        try {
          await supabase.functions.invoke('queue-email', {
            body: {
              recipient_email: 'admin@platform.com',
              template_name: 'admin_alert',
              template_data: {
                severity: errorData.severity,
                message: errorData.message,
                error_id: errorData.error_id,
                context: errorData.context,
                timestamp: new Date().toISOString(),
                subject: `URGENT: PO Creation Failed - ${errorData.severity.toUpperCase()}`
              },
              send_immediately: true
            }
          });
          console.log('[ErrorTracking] Admin alert sent for critical PO error');
        } catch (alertError) {
          console.error('[ErrorTracking] Failed to send admin alert:', alertError);
        }
      }
    } catch (error) {
      console.error('Failed to send error report:', error);
    }
  }
}

/**
 * Singleton instance of ErrorTrackingService
 * 
 * @example Basic usage
 * ```typescript
 * import { errorTracking } from '@/lib/errorTracking';
 * 
 * try {
 *   await someOperation();
 * } catch (error) {
 *   await errorTracking.captureError(error as Error);
 * }
 * ```
 */
export const errorTracking = new ErrorTrackingService();
